import { doc, getDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import type { BasicDetails, Address, BankDetails, PanVerification, GstVerification, AadhaarVerification } from '@/types/vendor';

/**
 * Fire-and-forget write to vendors/{uid} root document using merge so
 * each step only touches its own fields without overwriting other sections.
 */
function saveVendorData(uid: string, data: Record<string, unknown>): void {
  setDoc(
    doc(db, 'vendors', uid),
    { ...data, updatedAt: serverTimestamp() },
    { merge: true },
  ).catch((err) => console.error('[Firestore] Failed to save vendor data:', err));
}

export function saveMobileNumber(uid: string, mobileNumber: string): void {
  setDoc(
    doc(db, 'vendors', uid),
    { mobileNumber, updatedAt: serverTimestamp() },
    { merge: true },
  ).catch((err) => console.error('[Firestore] Failed to save mobile number:', err));
}

export function saveEntityType(uid: string, entityType: string): void {
  saveVendorData(uid, { entityType });
}

export function saveBasicDetails(uid: string, data: BasicDetails): void {
  saveVendorData(uid, { basicDetails: data });
}

export function saveAddress(uid: string, data: Address): void {
  saveVendorData(uid, { address: data });
}

export interface PanDocumentData {
  fileUrl: string;
  fileName: string;
  fileSize: number;
  storagePath: string;
  uploadedAt: Timestamp;
  isApproved: boolean;
}

export function savePanDetails(uid: string, panNumber: string, panName?: string): void {
  saveVendorData(uid, {
    panNumber,
    ...(panName ? { panName } : {}),
  });
}

export function savePanDocument(uid: string, data: PanDocumentData): void {
  saveVendorData(uid, { panDocument: data });
}

export function savePanData(uid: string, data: PanVerification): void {
  saveVendorData(uid, { pan: data });
}

export function saveGstData(uid: string, data: GstVerification): void {
  saveVendorData(uid, { gstin: data });
}

export function saveAadhaarData(uid: string, data: AadhaarVerification): void {
  saveVendorData(uid, { aadhaar: data });
}
export function saveGstDetails(uid: string, gstNumber: string, gstBusinessName?: string): void {
  saveVendorData(uid, { gstNumber, ...(gstBusinessName ? { gstBusinessName } : {}) });
}

export function saveBankDetails(uid: string, data: Partial<BankDetails>): void {
  saveVendorData(uid, { bankDetails: data });
}

export function saveDocuments(uid: string, documentTypes: string[]): void {
  saveVendorData(uid, { documentsSubmitted: { count: documentTypes.length, types: documentTypes } });
}

export interface LivePhotoData {
  downloadUrl: string;
  storagePath: string;
  capturedAt: Timestamp;
}

export function saveLivePhoto(uid: string, data: LivePhotoData): void {
  saveVendorData(uid, { livePhoto: data });
}

export function saveDeclarations(uid: string, gstAccepted: boolean, hlcAccepted: boolean): void {
  saveVendorData(uid, {
    declarations: {
      gstDeclarationAccepted: gstAccepted,
      hlcAgreementAccepted: hlcAccepted,
      acceptedAt: Timestamp.now(),
    },
  });
}

/**
 * Load the full vendor document from Firestore.
 * Returns null if the document does not exist or on error.
 */
export async function loadVendorData(uid: string): Promise<Record<string, unknown> | null> {
  try {
    const snapshot = await getDoc(doc(db, 'vendors', uid));
    if (!snapshot.exists()) return null;
    return snapshot.data() as Record<string, unknown>;
  } catch (err) {
    console.error('[Firestore] Failed to load vendor data:', err);
    return null;
  }
}

/**
 * Persist the step the user is moving INTO so it can be resumed on next login.
 * Uses dot-notation key so it does not overwrite other root fields.
 */
export function saveCurrentStep(uid: string, stepId: string): void {
  setDoc(
    doc(db, 'vendors', uid),
    { currentStepId: stepId, updatedAt: serverTimestamp() },
    { merge: true },
  ).catch((err) => console.error('[Firestore] Failed to save current step:', err));
}

/**
 * Persist minimal verification status (status + method + timestamp) for a single
 * verification type. Uses dot-notation key so only the given type is updated.
 */
export function saveVerificationStatus(
  uid: string,
  type: string,
  status: string,
  method: string | null,
): void {
  setDoc(
    doc(db, 'vendors', uid),
    {
      [`verificationStatus.${type}`]: { status, method, timestamp: Timestamp.now() },
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  ).catch((err) => console.error('[Firestore] Failed to save verification status:', err));
}

/**
 * Persist the list of consent categories that have been granted so consent state
 * can be restored on next login.
 */
export function saveGrantedConsents(uid: string, grantedCategories: string[]): void {
  saveVendorData(uid, { grantedConsents: grantedCategories });
}

/**
 * Persist the onboarding reference ID for the vendor.
 * Called once on first visit to the success page so the same ID
 * is shown on subsequent page loads.
 */
export function saveReferenceId(uid: string, referenceId: string): void {
  saveVendorData(uid, { referenceId });
}
